/**
 * Which of a note's answers the classifier's triggered rules were about.
 *
 * A triggered rule carries `evidence`: the text the model quoted or closely paraphrased. The model
 * does not say which question it came from, so the answer is found by matching the evidence back
 * against what the caregiver wrote -- the answer choices and the open comment.
 *
 * Matching is on the caregiver's own words and never on the question, because a word that only
 * appears in the question ("Did the patient fall?") is not evidence that anything happened.
 * Deliberately forgiving of paraphrase and deliberately strict about short evidence, so a
 * one-word "yes" does not light up every answer that says yes.
 */

type AnswerText = {
  answer: string[];
  comment: string | null;
};

/** Evidence shorter than this many meaningful words is only matched as an exact phrase. */
const MIN_EVIDENCE_WORDS = 2;
/** Share of the evidence's words that must appear in the answer for a paraphrase to count. */
const MIN_OVERLAP = 0.6;
/** Shortest answer or comment that may match by being quoted inside the evidence. */
const MIN_QUOTED_LENGTH = 6;
/** At most this many answers are shown under one rule. */
const MAX_MATCHES_PER_RULE = 3;

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

function words(normalized: string): Set<string> {
  return new Set(normalized.split(" ").filter((word) => word.length >= 3));
}

function scoreAnswer(evidence: string, answer: AnswerText): number {
  const normalizedEvidence = normalize(evidence);

  if (normalizedEvidence === "") {
    return 0;
  }

  const own = [answer.answer.join(" "), answer.comment ?? ""].map(normalize);

  // The evidence quotes the answer or the comment, or is a quote from inside them.
  for (const text of own) {
    if (text.length < MIN_QUOTED_LENGTH) {
      continue;
    }

    if (normalizedEvidence.includes(text) || text.includes(normalizedEvidence)) {
      return 1;
    }
  }

  const evidenceWords = words(normalizedEvidence);

  if (evidenceWords.size < MIN_EVIDENCE_WORDS) {
    return 0;
  }

  const ownWords = words(own.join(" "));
  let shared = 0;

  for (const word of evidenceWords) {
    if (ownWords.has(word)) {
      shared += 1;
    }
  }

  const overlap = shared / evidenceWords.size;

  return shared >= 2 && overlap >= MIN_OVERLAP ? overlap : 0;
}

/** For each rule, the indexes of the answers it was about, best match first. */
export function matchRulesToAnswers(
  rules: { evidence: string }[],
  answers: AnswerText[]
): number[][] {
  return rules.map((rule) =>
    answers
      .map((answer, index) => ({ index, score: scoreAnswer(rule.evidence, answer) }))
      .filter((match) => match.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_MATCHES_PER_RULE)
      .map((match) => match.index)
  );
}

/** Every answer index that some rule was about. */
export function triggeringAnswerIndexes(matches: number[][]): Set<number> {
  return new Set(matches.flat());
}
